import React from "react"
import pluralize from "pluralize"
import {
    IncludedAddOns,
    VerticalStack,
    Button,
    componentStyles,
} from "@mallardbay/lib-react-components"

import { AddOnCategory } from "~graphql/generated/graphql"
import type {
    AddOnQuoteFieldsFragment,
    PricingPackageFieldsFragment,
} from "~graphql/generated/graphql"
import CardWithErrorBanner from "~components/shared/card-with-warning-banner"
import { isSetGuestPricingPackage } from "~utils/helpers"
import COPY from "~config/copy-constants"
import Box from "~components/shared/todo-lib-react-components/box"

interface Props {
    readonly addOns: AddOnQuoteFieldsFragment[]
    readonly pricingPackage?: PricingPackageFieldsFragment | null
    readonly numberOfGuests: number
    readonly validationMessage?: string | null
    readonly isEditable?: boolean
    readonly onEditClick?: () => void
}

export default function CheckoutSummaryPackage({
    addOns,
    pricingPackage,
    numberOfGuests,
    validationMessage,
    isEditable,
    onEditClick,
}: Props) {
    const styles = useStyles()

    const { trophyAddOns, gearRentalAddOns, mealUpgradeAddOns, otherAddOns } =
        getAddOnsGroupedByCategory(addOns)

    const formattedNumberOfGuests = formatNumberOfGuests({
        numberOfGuests,
        pricingPackage,
    })

    return (
        <CardWithErrorBanner bannerMessage={validationMessage}>
            {pricingPackage && (
                <Box style={styles.packageName}>{pricingPackage.name}</Box>
            )}
            <VerticalStack>
                <Box>{formattedNumberOfGuests}</Box>
                {pricingPackage && (
                    <IncludedAddOns
                        trophyAddOns={pricingPackage.trophies}
                        gearRentalAddOns={pricingPackage.gear_rentals}
                        mealUpgradeAddOns={pricingPackage.meal_upgrades}
                        otherAddOns={pricingPackage.other_add_ons}
                    />
                )}
                <IncludedAddOns
                    title={COPY.ADDITIONAL_ADD_ONS_SELECTED}
                    trophyAddOns={trophyAddOns}
                    gearRentalAddOns={gearRentalAddOns}
                    mealUpgradeAddOns={mealUpgradeAddOns}
                    otherAddOns={otherAddOns}
                />
                {isEditable && (
                    <Button style={styles.editButton} onClick={onEditClick}>
                        {COPY.UPDATE}
                    </Button>
                )}
            </VerticalStack>
        </CardWithErrorBanner>
    )
}

function useStyles() {
    return componentStyles({
        packageName: {
            fontSize: "20px",
            fontWeight: 700,
            marginBottom: "12px",
        },
        editButton: {
            alignSelf: "flex-end",
        },
    })
}

function formatNumberOfGuests({
    numberOfGuests,
    pricingPackage,
}: {
    numberOfGuests: number
    pricingPackage?: PricingPackageFieldsFragment | null
}) {
    const isGroupPackage = isSetGuestPricingPackage(pricingPackage)

    const pluralizedGuests = pluralize(COPY.GUEST, numberOfGuests)
    const formattedNumberOfGuests = `${numberOfGuests} ${pluralizedGuests}`

    return isGroupPackage
        ? `${COPY.UP_TO} ${formattedNumberOfGuests}`
        : formattedNumberOfGuests
}

function getAddOnsGroupedByCategory(addOns: AddOnQuoteFieldsFragment[]) {
    const trophyAddOns = filterAddOnsByCategory(addOns, AddOnCategory.Trophy)
    const gearRentalAddOns = filterAddOnsByCategory(
        addOns,
        AddOnCategory.GunRental
    )
    const mealUpgradeAddOns = filterAddOnsByCategory(
        addOns,
        AddOnCategory.MealUpgrade
    )
    const otherAddOns = filterAddOnsByCategory(addOns, AddOnCategory.Other)

    return { trophyAddOns, gearRentalAddOns, mealUpgradeAddOns, otherAddOns }
}

function filterAddOnsByCategory(
    addOns: AddOnQuoteFieldsFragment[],
    category: AddOnCategory
) {
    return addOns.filter((addOn) => addOn.category === category)
}
